@if (!embedded()) {
  <div class="chat-box-header" [ngClass]="{ 'chat-box-header-clickable': hideComposer() }" (click)="onClickHeader()">
    @if (!!assistantAvatarUrl()) {
      <div class="chat-box-avatar">
        <glxy-avatar [src]="assistantAvatarUrl()" [name]="assistantName()"></glxy-avatar>
      </div>
    }
    <div class="chat-box-header-content">
      <h3 id="webchat-title" class="chat-box-title">
        {{ welcomeMessage() || 'INBOX.WEBCHAT.SETTINGS.WEBCHAT_WELCOME_MESSAGE_PLACEHOLDER' | translate }}
      </h3>
      <div class="chat-status">
        <webchat-status></webchat-status>
        <div class="chat-box-status-title">
          {{ 'INBOX.WEBCHAT.WIDGET.WE_RESPOND_IMMEDIATELY' | translate }}
        </div>
      </div>
    </div>
    @if (canClose()) {
      <button
        mat-icon-button
        matSuffix
        class="close-button"
        (click)="onClickClose()"
        [attr.aria-label]="'INBOX.WEBCHAT.WIDGET.CLOSE_WIDGET' | translate"
      >
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="currentColor">
          <path d="M0 0h24v24H0z" fill="none" />
          <path
            d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
          />
        </svg>
      </button>
    }
  </div>
}

<div class="chat-messages">
  <!-- Screen reader live announcements -->
  <div class="sr-only" aria-live="polite" aria-atomic="false" aria-relevant="additions text" #liveRegion>
    {{ liveAnnouncement() }}
  </div>

  @if (messageGroups().length > 0 || !embedded()) {
    <glxy-chat-container
      #chatContainer
      [animationDuration]="200"
      [customColor]="true"
      [downArrowText]="'WEBCHAT.NEW_MESSAGE_RECEIVED' | translate"
    >
      @if (isReplying()) {
        <glxy-chat-message-group
          [isTyping]="true"
          [showProfilePic]="!!assistantAvatarUrl()"
          [messageFrom]="assistantName()"
          [sentFromYou]="false"
          [profilePicUrl]="assistantAvatarUrl()"
        ></glxy-chat-message-group>
      }

      @for (group of messageGroups(); track group.id; let i = $index) {
        <glxy-chat-message-group
          [id]="'message-group-' + (messageGroups().length - i)"
          role="group"
          [tabindex]="messageGroups().length - i"
          [attr.aria-label]="
            ((group | isSentFromYou: currentParticipantId()) ? ('GALAXY.CHAT.YOU' | translate) : senderNameForGroup(group)) +
            ': ' +
            group.messageTexts
          "
          [type]="(group | isSentFromYou: currentParticipantId()) ? 'sent' : 'received'"
          [showProfilePic]="!(group | isSentFromYou: currentParticipantId()) && !!assistantAvatarUrl()"
          [sentFromYou]="group | isSentFromYou: currentParticipantId()"
          [messageFrom]="senderNameForGroup(group)"
          [profilePicUrl]="!(group | isSentFromYou: currentParticipantId()) ? assistantAvatarUrl() : undefined"
        >
          @for (message of group.messages; track trackById($index, message)) {
            <glxy-chat-message
              [messageText]="
                shouldTranslateBody(message.body)
                  ? (message.body
                    | replaceNewLine
                    | translate
                    | glxyMarkdown: { currentHostName: currentHostName() }
                    | linky)
                  : (message.body | replaceNewLine | glxyMarkdown: { currentHostName: currentHostName() } | linky)
              "
              [messageStatus]="mapMessageStatus(message)"
              [messageTime]="message.created | date: 'shortTime'"
              [messageIsMarkdown]="true"
            ></glxy-chat-message>

            <!-- Render image attachments (e.g. server-generated charts, DALE-943) that ride
                 the message's media. Mirrors the Inbox #mediaMessage image branch. -->
            @for (media of message.media ?? []; track media.mediaUrl) {
              @if (media.mediaContentType.includes('image')) {
                <img
                  class="webchat-message-media"
                  [class.sent]="group | isSentFromYou: currentParticipantId()"
                  [src]="media | getMessageMediaUrl"
                  alt="attachment"
                />
              }
            }

            @if (message | showBookingAvailability: messages$ | async) {
              <inbox-booking-availability-component
                class="booking-availability"
                [inputMessage]="message"
                (preferredBooking)="handleSetPreferredBooking($event)"
              ></inbox-booking-availability-component>
            }
          }
        </glxy-chat-message-group>

        @if (!!group.created) {
          <glxy-chat-divider-text [matTooltip]="group.created | date: 'mediumDate'">
            {{ group.created | date: 'mediumDate' }}
          </glxy-chat-divider-text>
        }
      }
    </glxy-chat-container>
  } @else if (!hideEmptyState()) {
    <div class="embedded-empty-state">
      <div class="chat-box-avatar">
        <glxy-avatar [src]="assistantAvatarUrl()" [name]="assistantName()" [width]="60"></glxy-avatar>
      </div>
      <div class="empty-state-headline">
        {{ 'INBOX.AI_CHAT.NO_MESSAGES.TITLE' | translate: { assistantName: assistantName() } }}
      </div>
    </div>
  }
</div>

@if (!hideComposer()) {
  <div class="chat-box-footer" [ngClass]="{ 'mini-view-composer': !fullView() }">
    <glxy-chat-composer
      #chatComposer
      (keydown)="handleInput($event)"
      [enterKeySendsMessage]="true"
      (send)="sendMessage($event)"
      [textAreaMinRows]="1"
      [textAreaMaxRows]="5"
      [maxlength]="maxMessageLength"
      [customColor]="true"
      [placeholder]="placeholder()"
      [sendText]="'INBOX.WEBCHAT.WIDGET.SEND' | translate"
      [processing]="processing()"
    ></glxy-chat-composer>
    @let partnerName = $safeNavigationMigration(poweredBy()?.name);
    @let partnerLink = $safeNavigationMigration(poweredBy()?.url);
    @if (showFooterContent() && !!partnerName) {
      <p class="powered-by">
        @if (!!partnerName && !!partnerLink) {
          <a target="_blank" href="{{ partnerLink }}">
            {{ 'INBOX.WEBCHAT.WIDGET.POWERED_BY' | translate }} {{ partnerName }}
          </a>
        } @else if (!!partnerName) {
          {{ 'INBOX.WEBCHAT.WIDGET.POWERED_BY' | translate }} {{ partnerName }}
        }
      </p>
    }
  </div>
}
